import React from "react";
import { Dropdown, Menu, Button, Space } from "antd";
import {
  EditOutlined,
  EllipsisOutlined,
  EyeOutlined,
  FileTextOutlined,
  SafetyCertificateOutlined,
  NotificationOutlined,
  AuditOutlined,
  SolutionOutlined,
  BookOutlined,
  LockOutlined,
  SwapOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import IntlMessages from "@iso/components/utility/intlMessages";
import moment from "moment";

export const formatApprovalStatus = (approval) => {
  if (!approval) {
    return "Not yet decided";
  }
  const statusLabel = approval.status === "approved" ? "Approved" : "Rejected";
  const decidedBy = approval.decided_by_name || "Unknown user";
  const datePart = approval.decided_at
    ? moment(approval.decided_at).format("DD/MM/YYYY")
    : "";
  const commentPart = approval.comment ? ` - ${approval.comment}` : "";
  return `${statusLabel} - ${decidedBy}${datePart ? ` - ${datePart}` : ""}${commentPart}`;
};

export const getApprovalColumn = ({ canDecideApproval, onApprovalOpen }) => ({
  title: "Approval",
  dataIndex: "latest_approval",
  key: "latest_approval",
  render: (approval, record) => (
    <Space direction="vertical" size={4} align="start">
      <span>{formatApprovalStatus(approval)}</span>
      {canDecideApproval && (
        <Button
          size="small"
          className="hs-approval-btn"
          onClick={() => onApprovalOpen(record)}
        >
          {approval ? "Update approval" : "Record approval"}
        </Button>
      )}
    </Space>
  ),
  width: 220,
});

/**
 * Whether an escalation rule has already been fired for an incident.
 *
 * Prefers the dispatch list the API now returns. A response from before
 * escalation rules existed has no such list, so fall back to the legacy MD
 * column for the built-in rule rather than offering to send it twice.
 *
 * @param {object} record
 * @param {{ code: string }} rule
 * @returns {boolean}
 */
const hasBeenNotified = (record, rule) => {
  if (Array.isArray(record.notified_rule_codes)) {
    return record.notified_rule_codes.includes(rule.code);
  }

  return rule.code === "notify_md" && record.md_notification_status === "sent";
};

export const getActionColumn = ({
  isSafetyAdmin,
  canManageIncidents,
  isOwner = false,
  currentUserId,
  notificationRules = [],
  handlers,
}) => {
  const normalizedCurrentUserId =
    typeof currentUserId === "number"
      ? currentUserId
      : Number(currentUserId);

  return {
    title: "",
    key: "actions",
    render: (_, record) => {
      const hasInvestigation =
        !!record.investigation_type &&
        record.investigation_type !== "notification_only";
      const isClosed = record.status === "closed";
      const isAssignedInvestigator =
        hasInvestigation &&
        normalizedCurrentUserId &&
        Number(record.investigator_id) === normalizedCurrentUserId;
      // Safety Admin and assigned investigator can always see investigation
      // Other users can only view after submission (not while in_progress)
      const canViewInvestigation =
        hasInvestigation &&
        (isSafetyAdmin ||
          isAssignedInvestigator ||
          !["investigation_in_progress"].includes(record.status));

      return (
        <Dropdown
          trigger={["click"]}
          overlay={
            <Menu>
              {canManageIncidents && (
                <Menu.Item
                  key="editDetails"
                  onClick={() => handlers.handleEditClick(record)}
                  icon={<EditOutlined />}
                >
                  <IntlMessages id="incidentPage.table.editDetails" />
                </Menu.Item>
              )}
              <Menu.Item
                key="history"
                onClick={() => handlers.handleHistoryClick(record)}
                icon={<EyeOutlined />}
              >
                <IntlMessages id="incidentPage.table.viewHistory" />
              </Menu.Item>
              <Menu.Item
                key="activities"
                onClick={() => handlers.handleActivityClick(record)}
                icon={<FileTextOutlined />}
              >
                <IntlMessages id="incidentPage.table.viewActivity" />
              </Menu.Item>

              {isSafetyAdmin &&
                record.status === "submitted_pending_safety_review" && (
                  <Menu.Item
                    key="classify"
                    onClick={() => handlers.handleClassifyNavigate(record)}
                    icon={<SafetyCertificateOutlined />}
                  >
                    Classify & Assign
                  </Menu.Item>
                )}

              {isSafetyAdmin &&
                notificationRules
                  .filter((rule) => !hasBeenNotified(record, rule))
                  .map((rule) => (
                    <Menu.Item
                      key={`notify-${rule.code}`}
                      onClick={() => handlers.handleNotify(record, rule)}
                      icon={<NotificationOutlined />}
                    >
                      {rule.label}
                    </Menu.Item>
                  ))}

              {canViewInvestigation && (
                <Menu.Item
                  key="investigation"
                  onClick={() => handlers.handleInvestigationClick(record)}
                  icon={<AuditOutlined />}
                >
                  View Investigation
                </Menu.Item>
              )}

              {isSafetyAdmin && hasInvestigation && !isClosed && (
                <Menu.Item
                  key="changeInvestigationType"
                  onClick={() => handlers.handleChangeInvestigationType(record)}
                  icon={<SwapOutlined />}
                >
                  <IntlMessages id="incidentPage.table.changeInvestigationType" />
                </Menu.Item>
              )}

              {(isSafetyAdmin || canManageIncidents) && (
                <Menu.Item
                  key="correctiveActions"
                  onClick={() => handlers.handleCorrectiveActionsClick(record)}
                  icon={<SolutionOutlined />}
                >
                  Corrective Actions
                </Menu.Item>
              )}

              {(isSafetyAdmin || canManageIncidents) && (
                <Menu.Item
                  key="lessonsLearned"
                  onClick={() => handlers.handleLessonsLearnedClick(record)}
                  icon={<BookOutlined />}
                >
                  Lessons Learned
                </Menu.Item>
              )}

              {!isClosed && (isSafetyAdmin || canManageIncidents) && (
                <Menu.Item
                  key="close"
                  onClick={() => handlers.handleCloseIncident(record)}
                  icon={<LockOutlined />}
                >
                  Close Incident
                </Menu.Item>
              )}

              {isOwner && handlers.handleDeleteClick && (
                <Menu.Item
                  key="delete"
                  danger
                  onClick={() => handlers.handleDeleteClick(record)}
                  icon={<DeleteOutlined />}
                >
                  Delete Incident
                </Menu.Item>
              )}
            </Menu>
          }
        >
          <Button type="text" icon={<EllipsisOutlined />} />
        </Dropdown>
      );
    },
    width: 80,
    align: "center",
  };
};
